import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { supabase } from '../lib/supabaseClient';

// Re-export useQueryClient for convenience
export { useQueryClient } from '@tanstack/react-query';

// Tipos para las consultas
export interface Quote {
  id: string;
  user_id: string;
  name: string;
  email: string;
  phone: string;
  pickup_location: string;
  delivery_location: string;
  vehicle_type: string;
  estimated_price: number;
  final_price?: number;
  commission_amount?: number;
  carrier_payout?: number;
  commission_rate?: number;
  service_paid?: boolean;
  status:
    | 'pending'
    | 'confirmed'
    | 'in_review'
    | 'ready_for_payment'
    | 'paid'
    | 'assigned'
    | 'scheduled'
    | 'in_transit'
    | 'delivered'
    | 'post_delivery_review'
    | 'closed'
    | 'cancelled';
  assigned_carrier_id?: string;
  created_at: string;
  updated_at: string;
}

export interface CarrierRegistration {
  id: string;
  user_id: string;
  company_name: string;
  contact_name: string;
  email: string;
  phone: string;
  dot_number: string;
  equipment_type: string;
  paid?: boolean;
  amount: number | null;
  created_at: string;
  updated_at: string;
  mc_number?: string | null;
  insurance_policy_number?: string | null;
  insurance_provider?: string | null;
  insurance_coverage_amount?: number | null;
  insurance_expiration_date?: string | null;
  fmcsa_bipd_insurance_on_file?: string | null;
  fmcsa_bipd_insurance_required?: string | null;
  fmcsa_bipd_required_amount?: string | null;
  fmcsa_insurance_verified?: boolean;
  fmcsa_insurance_verified_date?: string | null;
  dot_verification_status?: 'pending' | 'verified' | 'failed' | 'expired';
  dot_verification_date?: string | null;
  dot_verification_notes?: string | null;
  mc_verification_status?: 'pending' | 'verified' | 'failed' | 'expired';
  mc_verification_date?: string | null;
  mc_verification_notes?: string | null;
  compliance_status?: 'pending' | 'compliant' | 'non_compliant' | 'expired_documents';
  compliance_last_checked?: string | null;
  documents_uploaded?: boolean;
  // New operational fields
  fleet_size?: number | null;
  service_areas?: string[] | null;
  preferred_lanes?: string[] | null;
  equipment_details?: Record<string, any> | null;
  safety_rating?: string | null;
  operating_radius_km?: number | null;
  last_active_at?: string | null;
  internal_notes?: string | null;
}

export interface Payment {
  id: string;
  user_id: string;
  stripe_session_id: string;
  amount_total: number;
  customer_email: string;
  service_type: string;
  record_id: string;
  status: string;
  created_at: string;
}



// Query Keys
export const queryKeys = {
  quotes: ['quotes'] as const,
  quote: (id: string) => ['quotes', id] as const,
  carrierRegistrations: ['carrierRegistrations'] as const,
  carrierRegistration: (id: string) => ['carrierRegistrations', id] as const,
  payments: ['payments'] as const,
  payment: (id: string) => ['payments', id] as const,
  
  dashboardStats: ['dashboardStats'] as const,
  userQuotes: (userId: string) => ['quotes', 'user', userId] as const,
  userCarriers: (userId: string) => ['carrierRegistrations', 'user', userId] as const,
};

// Hooks para Quotes
export const useQuotes = (options?: UseQueryOptions<Quote[], Error>) => {
  return useQuery({
    queryKey: queryKeys.quotes,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    staleTime: 30000, // 30 segundos
    refetchInterval: 60000, // Refetch cada minuto
    ...options,
  });
};

export const useQuote = (id: string, options?: UseQueryOptions<Quote, Error>) => {
  return useQuery({
    queryKey: queryKeys.quote(id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!id,
    ...options,
  });
};

export const useUserQuotes = (userId: string, options?: UseQueryOptions<Quote[], Error>) => {
  return useQuery({
    queryKey: queryKeys.userQuotes(userId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('quotes')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
    staleTime: 30000,
    ...options,
  });
};

// Hooks para Carrier Registrations
export const useCarrierRegistrations = (options?: UseQueryOptions<CarrierRegistration[], Error>) => {
  return useQuery({
    queryKey: queryKeys.carrierRegistrations,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('carrier_registrations')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    staleTime: 30000,
    refetchInterval: 60000,
    ...options,
  });
};

export const useUserCarriers = (userId: string, options?: UseQueryOptions<CarrierRegistration[], Error>) => {
  return useQuery({
    queryKey: queryKeys.userCarriers(userId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('carrier_registrations')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!userId,
    staleTime: 30000,
    ...options,
  });
};

// Hooks para Payments - using the more complete version with filters below

// Dashboard Stats Hook
export const useDashboardStats = (options?: UseQueryOptions<any, Error>) => {
  return useQuery({
    queryKey: queryKeys.dashboardStats,
    queryFn: async () => {
      // Obtener estadísticas en paralelo
      const [quotesResult, carriersResult, paymentsResult] = await Promise.all([
        supabase.from('quotes').select('*'),
        supabase.from('carrier_registrations').select('*'),
        supabase.from('payments').select('*'),
      ]);

      if (quotesResult.error) throw quotesResult.error;
      if (carriersResult.error) throw carriersResult.error;
      if (paymentsResult.error) throw paymentsResult.error;

      const quotes = quotesResult.data || [];
      const carriers = carriersResult.data || [];
      const payments = paymentsResult.data || [];

      return {
        totalQuotes: quotes.length,
        pendingQuotes: quotes.filter(q => ['pending', 'in_review'].includes(q.status)).length,
        confirmedQuotes: quotes.filter(q => ['confirmed', 'ready_for_payment', 'paid'].includes(q.status)).length,
        activeShipments: quotes.filter(q => ['scheduled', 'assigned', 'in_transit'].includes(q.status)).length,
        completedQuotes: quotes.filter(q => ['closed', 'completed', 'delivered'].includes(q.status)).length,
        totalCarriers: carriers.length,
        verifiedCarriers: carriers.filter(c => c.compliance_status === 'compliant').length,
        totalPayments: payments.length,
        totalRevenue: payments.reduce((sum, p) => sum + p.amount_total, 0) / 100,
        recentQuotes: quotes.slice(0, 5),
        recentCarriers: carriers.slice(0, 5),
        recentPayments: payments.slice(0, 5),
      };
    },
    staleTime: 30000,
    refetchInterval: 60000,
    ...options,
  });
};

// Mutations con Optimistic Updates
export const useCreateQuote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (quoteData: Omit<Quote, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('quotes')
        .insert([quoteData])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: (newQuote) => {
      // Optimistic update
      queryClient.setQueryData(queryKeys.quotes, (old: Quote[] | undefined) => {
        if (!old) return [newQuote];
        return [newQuote, ...old];
      });
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
      queryClient.invalidateQueries({ queryKey: queryKeys.userQuotes(newQuote.user_id) });
    },
  });
};

export const useDeleteQuote = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('quotes')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return id;
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.quotes });
      
      const previousQuotes = queryClient.getQueryData(queryKeys.quotes);
      
      // Optimistic update
      queryClient.setQueryData(queryKeys.quotes, (old: Quote[] | undefined) => {
        if (!old) return old;
        return old.filter(quote => quote.id !== id);
      });
      
      return { previousQuotes };
    },
    onError: (err, variables, context) => {
      if (context?.previousQuotes) {
        queryClient.setQueryData(queryKeys.quotes, context.previousQuotes);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.quotes });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
    },
  });
};

// Mutation para verificar DOT/MC
export const useVerifyDOTMC = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ carrierId, dotNumber, mcNumber }: { carrierId: string; dotNumber: string; mcNumber?: string }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('You must be logged in to verify DOT/MC numbers');
      }

      const response = await fetch('/.netlify/functions/verify-dot-mc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          carrierId,
          dotNumber,
          mcNumber: mcNumber || undefined,
        }),
      });

      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Verification failed');
      }

      return result;
    },
    onMutate: async ({ carrierId }) => {
      // Cancelar queries para evitar conflictos
      await queryClient.cancelQueries({ queryKey: ['carrierRegistrations'] });
      
      // Obtener datos anteriores para rollback en caso de error
      const previousCarriers = queryClient.getQueryData<CarrierRegistration[]>(['carrierRegistrations']);
      
      // Optimistic update - mostrar como "pending" mientras se verifica
      queryClient.setQueryData<CarrierRegistration[]>(['carrierRegistrations'], (old) => {
        if (!old) return old;
        return old.map(carrier => 
          carrier.id === carrierId 
            ? { 
                ...carrier, 
                dot_verification_status: 'pending' as const,
                mc_verification_status: carrier.mc_number ? ('pending' as const) : carrier.mc_verification_status,
              }
            : carrier
        );
      });
      
      return { previousCarriers };
    },
    onError: (err, variables) => {
      // Actualizar estado a "failed" en lugar de hacer rollback completo
      queryClient.setQueryData<CarrierRegistration[]>(['carrierRegistrations'], (old) => {
        if (!old) return old;
        return old.map(carrier => 
          carrier.id === variables.carrierId 
            ? { 
                ...carrier, 
                dot_verification_status: 'failed' as const,
                dot_verification_date: new Date().toISOString(),
                dot_verification_notes: err.message || 'Verification failed',
                mc_verification_status: carrier.mc_number ? ('failed' as const) : carrier.mc_verification_status,
                mc_verification_date: carrier.mc_number ? new Date().toISOString() : carrier.mc_verification_date,
                mc_verification_notes: carrier.mc_number ? (err.message || 'Verification failed') : carrier.mc_verification_notes,
              }
            : carrier
        );
      });
    },
    onSuccess: (result, variables) => {
      // Actualizar el estado optimista con los datos reales de la respuesta
      // La función retorna { success: true, verification: { dotNumber, mcNumber, status, insurance, ... } }
      if (result.success && result.verification) {
        queryClient.setQueryData<CarrierRegistration[]>(['carrierRegistrations'], (old) => {
          if (!old) return old;
          return old.map(carrier => {
            if (carrier.id !== variables.carrierId) return carrier;
            
            // Actualizar información de verificación DOT/MC
            const updatedCarrier: CarrierRegistration = {
              ...carrier,
              dot_verification_status: 'verified' as const,
              dot_verification_date: new Date().toISOString(),
              dot_verification_notes: `Verified via FMCSA. Status: ${result.verification.status || 'Verified'}`,
            };

            // Actualizar MC si existe
            if (result.verification.mcNumber) {
              updatedCarrier.mc_verification_status = 'verified' as const;
              updatedCarrier.mc_verification_date = new Date().toISOString();
              updatedCarrier.mc_verification_notes = `Verified via FMCSA. Status: ${result.verification.status || 'Verified'}`;
            }

            // Actualizar información de seguro de FMCSA si está disponible
            if (result.verification.insurance) {
              updatedCarrier.fmcsa_bipd_insurance_on_file = result.verification.insurance.bipdInsuranceOnFile || null;
              updatedCarrier.fmcsa_bipd_insurance_required = result.verification.insurance.bipdInsuranceRequired || null;
              updatedCarrier.fmcsa_bipd_required_amount = result.verification.insurance.bipdRequiredAmount || null;
              updatedCarrier.fmcsa_insurance_verified = result.verification.insurance.hasValidInsurance || false;
              updatedCarrier.fmcsa_insurance_verified_date = new Date().toISOString();
            }

            return updatedCarrier;
          });
        });
      }
      // Invalidar queries para asegurar sincronización con BD
      queryClient.invalidateQueries({ queryKey: queryKeys.carrierRegistrations });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.carrierRegistrations });
      queryClient.invalidateQueries({ queryKey: queryKeys.dashboardStats });
    },
  });
};

// Configuración global de React Query
export const reactQueryConfig = {
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30000, // 30 segundos
      gcTime: 5 * 60 * 1000, // 5 minutos
    },
    mutations: {
      retry: 1,
    },
  },
};

// Hook para pagos generales
export const usePayments = (options?: {
  dateFilter?: '7d' | '30d' | '90d' | 'all';
  serviceFilter?: 'all' | 'quote' | 'carrier' | 'transport';
}) => {
  return useQuery({
    queryKey: ['payments', options?.dateFilter, options?.serviceFilter],
    queryFn: async () => {
      let query = supabase
        .from('payments')
        .select('*')
        .order('created_at', { ascending: false });

      // Apply date filter
      if (options?.dateFilter && options.dateFilter !== 'all') {
        const days = parseInt(options.dateFilter.replace('d', ''));
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - days);
        query = query.gte('created_at', startDate.toISOString());
      }

      // Apply service type filter
      if (options?.serviceFilter && options.serviceFilter !== 'all') {
        query = query.eq('service_type', options.serviceFilter);
      }

      const { data, error } = await query;
      if (error) throw error;

      return data || [];
    },
    staleTime: 30000,
    refetchInterval: 60000,
  });
};

